/**
 * Waiting List Store — Stato globale del drawer "Lista d'attesa".
 *
 * Aperto dal pulsante "Inserisci il tuo nominativo in lista d'attesa" che
 * compare nel payment selector quando i posti sono esauriti e la lista
 * d'attesa è attiva. Replica il form legaci di acquisizione dati
 * (vedi Elearning::listaAttesaSubmit): nome, disciplina, email, messaggio.
 *
 * Uso nei template:
 *   $store.waitingList.open(courseId, courseTitle)
 *   $store.waitingList.drawerOpen   — visibilità del drawer
 *   $store.waitingList.submit()     — invio richiesta
 *
 * Il catalogo professioni→discipline (label "Professione, Disciplina") viene
 * caricato lazy da GET /v1/disciplines alla prima apertura.
 */
import { readProfession } from '../utils/profession-cookie.js';

export function registerWaitingListStore(Alpine) {
    Alpine.store('waitingList', {
        drawerOpen: false,
        loading: false,
        submitError: '',
        success: false,

        // Contesto corso
        courseId: null,
        courseTitle: '',

        // Catalogo discipline (caricato lazy da /v1/disciplines)
        disciplines: [],            // [{ id, label }]
        disciplinesLoaded: false,
        loadingDisciplines: false,

        // Discipline dell'utente collegato (chiavi "occupation_id|discipline_id"),
        // usate per preselezionare la disciplina nel menu
        userDisciplineKeys: [],

        // Dati form
        form: {
            name: '',
            email: '',
            discipline: '',
            message: '',
        },
        errors: {},

        open(courseId, courseTitle) {
            this.courseId    = courseId || null;
            this.courseTitle = courseTitle || '';
            this._reset();
            this.drawerOpen = true;
            document.body.style.overflow = 'hidden';
            this._loadDisciplines();
            this._loadUserDiscipline();
        },

        async _loadDisciplines() {
            if (this.disciplinesLoaded || this.loadingDisciplines) return;
            this.loadingDisciplines = true;
            try {
                const apiBase = window.IKOS?.apiBase || '';
                const res = await fetch(`${apiBase}/v1/disciplines`);
                const data = await res.json();
                this.disciplines = (data._embedded?.items ?? []).map(d => ({ id: d.id, label: d.label }));
                this.disciplinesLoaded = true;
                this._applyUserDiscipline();
            } catch (e) {
                this.disciplines = [];
            } finally {
                this.loadingDisciplines = false;
            }
        },

        // Carica le discipline dell'utente collegato per preselezionare il menu
        async _loadUserDiscipline() {
            const authToken = window.IKOS?.authToken;
            if (!authToken) return;
            if (this.userDisciplineKeys.length) { this._applyUserDiscipline(); return; }
            try {
                const apiBase = window.IKOS?.apiBase || '';
                const headers = { 'Accept': 'application/json' };
                if (authToken !== true) headers['Auth-Token'] = authToken;
                const res = await fetch(`${apiBase}/v1/users/me`, { headers });
                if (!res.ok) return;
                const data = await res.json();
                this.userDisciplineKeys = (data.disciplines ?? [])
                    .filter(d => d.occupation_id && d.discipline_id)
                    .map(d => `${d.occupation_id}|${d.discipline_id}`);
                this._applyUserDiscipline();
            } catch (e) {
                /* nessuna preselezione: il menu resta su "Seleziona una disciplina" */
            }
        },

        // Preseleziona la prima disciplina dell'utente presente nel catalogo.
        // Richiede sia il catalogo sia le chiavi utente caricati; non sovrascrive
        // una scelta già fatta manualmente dall'utente.
        _applyUserDiscipline() {
            if (this.form.discipline) return;
            if (!this.disciplines.length) return;
            for (const key of this.userDisciplineKeys) {
                const match = this.disciplines.find(d => d.id === key);
                if (match) { this.form.discipline = match.label; return; }
            }
            // Ospite: professione scelta nel catalogo (cookie). Con la disciplina se c'e',
            // altrimenti la prima della professione (unica per quasi tutte).
            const known = readProfession();
            if (!known?.id) return;
            const exact = known.discipline ? this.disciplines.find(d => d.id === `${known.id}|${known.discipline}`) : null;
            const match = exact || this.disciplines.find(d => d.id.startsWith(`${known.id}|`));
            if (match) this.form.discipline = match.label;
        },

        close() {
            this.drawerOpen = false;
            document.body.style.overflow = '';
        },

        _reset() {
            this.loading = false;
            this.submitError = '';
            this.success = false;
            this.errors = {};
            this.form = { name: '', email: '', discipline: '', message: '' };

            // Se l'utente è collegato, precompila nome ed email dai suoi dati
            const u = window.IKOS?.currentUser;
            if (u) {
                this.form.name  = [u.name, u.surname].filter(Boolean).join(' ').trim();
                this.form.email = u.email || '';
            }
        },

        _validate() {
            const e = {};
            if (!this.form.name.trim())  e.name  = 'Inserisci il tuo nome';
            if (!this.form.email.trim()) {
                e.email = 'Inserisci il tuo indirizzo mail';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
                e.email = 'Inserisci un indirizzo mail valido';
            }
            this.errors = e;
            return Object.keys(e).length === 0;
        },

        async submit() {
            if (this.loading) return;
            this.submitError = '';
            if (!this._validate()) return;
            if (!this.courseId) { this.submitError = 'Corso non valido.'; return; }

            this.loading = true;
            try {
                const apiBase = window.IKOS?.apiBase || '';
                const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
                const authToken = window.IKOS?.authToken;
                if (authToken && authToken !== true) headers['Auth-Token'] = authToken;

                const res = await fetch(`${apiBase}/v1/courses/${this.courseId}/waiting-list`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        name:       this.form.name.trim(),
                        email:      this.form.email.trim(),
                        discipline: this.form.discipline,
                        message:    this.form.message.trim(),
                    }),
                });
                const data = await res.json().catch(() => ({}));

                if (!res.ok) {
                    // 422: validation_messages = { campo: { regola: messaggio } }
                    if (res.status === 422 && data.validation_messages) {
                        const e = {};
                        Object.entries(data.validation_messages).forEach(([field, rules]) => {
                            e[field] = Object.values(rules || {})[0] || 'Valore non valido';
                        });
                        this.errors = e;
                    }
                    this.submitError = data.detail || 'Errore durante l\'invio dei dati. Riprova.';
                    return;
                }
                this.success = true;
            } catch (err) {
                this.submitError = 'Errore di connessione. Riprova.';
            } finally {
                this.loading = false;
            }
        },
    });
}